import type { KkmExecutionResult, KkmServerCommand, KkmServerProtocolProfile } from '@/shared/api';

import { hasKkmBankEvidence, KKM_SERVER_2_3_16_36_PROFILE } from '@/shared/api';

import { toRubles } from './kkm-server-receipt-mapper';

export interface MapCardCancelCommandInput {
  /** Сумма карточной части платежа в копейках. */
  amount: number;
  /** Отмена повторяема: у каждой попытки свой `IdCommand`, иначе KKMServer ответит кэшем. */
  idCommand: string;
  terminalDeviceId: string;
  /** `UniversalID` принятой оплаты; при неясном исходе его нет. */
  universalId: string | null;
}

/**
 * Итог отмены платежа на терминале:
 * - `reversed` — команда выполнена, деньги вернутся клиенту;
 * - `not_charged` — терминал ответил, что отменять нечего;
 * - `failed` — любой другой ответ, платёж остаётся неясным.
 */
export type CardCancelOutcome = 'failed' | 'not_charged' | 'reversed';

export function mapCardCancelCommand(input: MapCardCancelCommandInput): KkmServerCommand {
  return terminalCommand('CancelPaymentByPaymentCard', input, input.universalId ?? '');
}

/**
 * Возврат на карту по отменённому платежу другой смены: исходного `UniversalID` нет,
 * терминал находит операцию по RRN со слипа или из выписки клиента.
 */
export function mapCardReturnCommand(
  input: Omit<MapCardCancelCommandInput, 'universalId'> & { rrn: string },
): KkmServerCommand {
  return terminalCommand('ReturnPaymentByPaymentCard', input, rrnUniversalId(input.rrn));
}

/** `UniversalID`, по которому терминал находит операцию по RRN: для отмены и для возврата. */
export function rrnUniversalId(rrn: string): string {
  // ponytail: формат `RRN:<rrn>` как в UniversalID Сбера; подтверждается на стенде (Task 1).
  return `RRN:${rrn}`;
}

function terminalCommand(
  command: KkmServerCommand['Command'],
  input: Omit<MapCardCancelCommandInput, 'universalId'>,
  universalId: string,
): KkmServerCommand {
  return {
    Amount: toRubles(input.amount),
    Command: command,
    IdCommand: input.idCommand,
    IdDevice: input.terminalDeviceId,
    NumDevice: 0,
    Timeout: KKM_SERVER_2_3_16_36_PROFILE.timeouts.acquiring.commandTimeoutSeconds,
    UniversalID: universalId,
  };
}

/**
 * Классификация закрыта на отказ: «списания не было» читается только по коду из профиля
 * и только в ответе без следов проведённой операции.
 */
export function classifyCardCancel(
  result: KkmExecutionResult,
  profile: KkmServerProtocolProfile = KKM_SERVER_2_3_16_36_PROFILE,
): CardCancelOutcome {
  if (result.kind === 'ok') return 'reversed';
  if (result.kind !== 'error' || result.error.vendorCode === null) return 'failed';

  return !hasKkmBankEvidence(result.acquiring, result.response) &&
    profile.cancelNothingChargedCodes.includes(result.error.vendorCode)
    ? 'not_charged'
    : 'failed';
}
